/**
 * Aerodrome geometry — the platform's own drawing, not a basemap.
 *
 * Shapes follow `geo.py` (`list_layers`, `list_features`): a layer catalogue
 * the pack declares (`packs/airports/layers/airfield.yaml`) and a GeoJSON
 * FeatureCollection per layer in WGS-84. There is no useful public basemap
 * for an airfield; the drawing IS the map.
 *
 * Projection is equirectangular about the site centre, in metres. Over an
 * aerodrome (a few kilometres) the error against a proper UTM projection is
 * well under a metre, and it needs no library — the console proved both.
 */

export type LonLat = [lon: number, lat: number];

export type Geometry =
  | { type: "Point"; coordinates: LonLat }
  | { type: "MultiPoint"; coordinates: LonLat[] }
  | { type: "LineString"; coordinates: LonLat[] }
  | { type: "MultiLineString"; coordinates: LonLat[][] }
  | { type: "Polygon"; coordinates: LonLat[][] }
  | { type: "MultiPolygon"; coordinates: LonLat[][][] };

export interface LayerStyle {
  /** A literal colour. A pack rule ({by: attribute}) cannot be evaluated here and is dropped. */
  fill?: string;
  stroke?: string;
  widthPx?: number;
  opacity?: number;
}

export interface MapLayer {
  key: string;
  name: string;
  level?: string;
  zIndex: number;
  visibleDefault: boolean;
  style: LayerStyle;
  featureCount: number;
}

export interface MapFeature {
  id: string;
  layer: string;
  kind?: string;
  assetId?: string;
  geometry: Geometry;
  /** Pack attributes: tag, name, asset_type, zone, marking_type, colour … */
  props: Record<string, unknown>;
}

/* ---- projection ------------------------------------------------------------- */

export interface Projection {
  centre: LonLat;
  /** metres per degree of longitude at the centre latitude */
  kx: number;
  /** metres per degree of latitude */
  ky: number;
  toXY(p: LonLat): [x: number, y: number];
  toLonLat(x: number, y: number): LonLat;
}

const M_PER_DEG_LAT = 111_320;

/** Equirectangular about `centre`; y grows southwards so it maps straight onto screen space. */
export function projectionAbout(centre: LonLat): Projection {
  const kx = M_PER_DEG_LAT * Math.cos((centre[1] * Math.PI) / 180);
  const ky = M_PER_DEG_LAT;
  return {
    centre,
    kx,
    ky,
    toXY: ([lon, lat]) => [(lon - centre[0]) * kx, (centre[1] - lat) * ky],
    toLonLat: (x, y) => [centre[0] + x / kx, centre[1] - y / ky],
  };
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function emptyBounds(): Bounds {
  return { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
}

export function extend(b: Bounds, [x, y]: [number, number]): void {
  if (x < b.minX) b.minX = x;
  if (x > b.maxX) b.maxX = x;
  if (y < b.minY) b.minY = y;
  if (y > b.maxY) b.maxY = y;
}

export function isFinite(b: Bounds): boolean {
  return Number.isFinite(b.minX) && Number.isFinite(b.maxX);
}

export function eachPosition(g: Geometry, fn: (p: LonLat) => void): void {
  switch (g.type) {
    case "Point":
      fn(g.coordinates);
      return;
    case "MultiPoint":
    case "LineString":
      g.coordinates.forEach(fn);
      return;
    case "MultiLineString":
    case "Polygon":
      g.coordinates.forEach((r) => r.forEach(fn));
      return;
    case "MultiPolygon":
      g.coordinates.forEach((poly) => poly.forEach((r) => r.forEach(fn)));
      return;
  }
}

export function boundsOf(features: readonly { geometry: Geometry }[], P: Projection): Bounds {
  const b = emptyBounds();
  for (const f of features) eachPosition(f.geometry, (p) => extend(b, P.toXY(p)));
  return b;
}

/** Centre of the features' extent in lon/lat — the projection origin when the site gives none. */
export function centroidOf(features: readonly { geometry: Geometry }[]): LonLat | undefined {
  let minLon = Infinity,
    maxLon = -Infinity,
    minLat = Infinity,
    maxLat = -Infinity;
  for (const f of features)
    eachPosition(f.geometry, ([lon, lat]) => {
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    });
  if (!Number.isFinite(minLon)) return undefined;
  return [(minLon + maxLon) / 2, (minLat + maxLat) / 2];
}

/** SVG path data for a line or polygon geometry; points are drawn separately. */
export function pathFor(g: Geometry, P: Projection): string | null {
  const ring = (coords: LonLat[]) =>
    coords
      .map((c, i) => {
        const [x, y] = P.toXY(c);
        return `${i ? "L" : "M"}${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join("");
  switch (g.type) {
    case "Polygon":
      return g.coordinates.map((r) => ring(r) + "Z").join("");
    case "MultiPolygon":
      return g.coordinates
        .flat()
        .map((r) => ring(r) + "Z")
        .join("");
    case "LineString":
      return ring(g.coordinates);
    case "MultiLineString":
      return g.coordinates.map(ring).join("");
    default:
      return null;
  }
}

export function pointsOf(g: Geometry): LonLat[] {
  if (g.type === "Point") return [g.coordinates];
  if (g.type === "MultiPoint") return g.coordinates;
  return [];
}

/** A representative point for any geometry: the point itself, else the mean of its positions. */
export function anchorOf(g: Geometry): LonLat {
  if (g.type === "Point") return g.coordinates;
  let n = 0,
    lon = 0,
    lat = 0;
  eachPosition(g, (p) => {
    n++;
    lon += p[0];
    lat += p[1];
  });
  return n ? [lon / n, lat / n] : [0, 0];
}

/* ---- default styles for the airports pack's layers ------------------------------ */

/**
 * Where the pack declares a literal colour it wins; where it declares a rule
 * (pavement fill by condition score) these stand in. Chosen to read on the
 * dark console and to keep the state colours (red/amber/green) free for
 * work, alerts and findings.
 */
export const LAYER_DEFAULTS: Record<string, LayerStyle> = {
  pavement: { fill: "#243241", stroke: "#94a3b8", widthPx: 0.8, opacity: 0.95 },
  markings: { stroke: "#e5e7eb", widthPx: 1 },
  agl: { fill: "#fbbf24", widthPx: 1.6 },
  agl_circuits: { stroke: "#7c6f3a", widthPx: 0.6, opacity: 0.6 },
  signs: { fill: "#fde68a", widthPx: 1.6 },
  buildings: { fill: "#334155", stroke: "#64748b", widthPx: 0.6 },
  roads: { stroke: "#6b5b3f", widthPx: 1.2, opacity: 0.8 },
  perimeter: { stroke: "#ef4444", widthPx: 0.8, opacity: 0.5 },
  mep_utilities: { stroke: "#5b7c99", widthPx: 0.6, opacity: 0.6 },
  drainage: { stroke: "#3b82f6", widthPx: 0.7, opacity: 0.6 },
  grid: { stroke: "#334155", widthPx: 0.4, opacity: 0.5 },
};

export function styleFor(layer: MapLayer | undefined, key: string): LayerStyle {
  const d = LAYER_DEFAULTS[key] ?? { stroke: "#8ca3b3", widthPx: 0.8 };
  return { ...d, ...layer?.style };
}
